import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { FileText, RefreshCw, TrendingUp, ArrowDownCircle, ArrowUpCircle, Banknote } from "lucide-react";
import { formatCurrency, API_URL } from "../../lib/utils";
import { printPeriodReport, PERIOD_LABELS } from "../../lib/cashPrint";

const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (s) => new Date(s + "T12:00:00").toLocaleDateString("pt-PT");

const Stat = ({ label, value, icon: Icon, tone = "", testId }) => (
  <div className="rounded-lg border p-3">
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</p>
    <p className={`font-mono font-bold text-lg mt-1 ${tone}`} data-testid={testId}>{value}</p>
  </div>
);

export const CashReportsPanel = ({ headers, settings, refreshKey }) => {
  const [period, setPeriod] = useState("daily");
  const [date, setDate] = useState(today());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/cash/report?period=${period}&date=${date}`, { headers, withCredentials: true });
      setReport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gerar relatório");
    } finally {
      setLoading(false);
    }
  }, [period, date]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load, refreshKey]);

  const t = report?.totals || {};
  return (
    <Card data-testid="cash-reports-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Relatórios de Caixa</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-md border overflow-hidden">
              {Object.entries(PERIOD_LABELS).map(([k, l]) => (
                <button key={k} onClick={() => setPeriod(k)} className={`px-3 h-9 text-sm font-medium transition-colors ${period === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`cash-report-period-${k}`}>{l}</button>
              ))}
            </div>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="cash-report-date" />
            <Button variant="outline" size="icon" onClick={load} title="Atualizar" data-testid="cash-report-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
            <Button onClick={() => report && printPeriodReport(report, settings)} disabled={!report} className="gap-2" data-testid="cash-report-pdf-btn"><FileText className="w-4 h-4" /> Gerar PDF</Button>
          </div>
        </div>
        {report && (
          <p className="text-xs text-muted-foreground" data-testid="cash-report-range">
            Período: {report.start_date === report.end_date ? fmtDate(report.start_date) : `${fmtDate(report.start_date)} a ${fmtDate(report.end_date)}`} · {report.movements_count} movimentos · {report.sessions_count} sessão(ões)
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat label={`Vendas (${t.sales_count || 0})`} value={formatCurrency(t.sales || 0)} icon={TrendingUp} testId="cash-report-sales" />
          <Stat label="Entradas" value={formatCurrency(t.entries || 0)} icon={ArrowDownCircle} tone="text-emerald-600" testId="cash-report-entries" />
          <Stat label="Saídas" value={formatCurrency(t.exits || 0)} icon={ArrowUpCircle} tone="text-red-600" testId="cash-report-exits" />
          <Stat label="Numerário líquido" value={formatCurrency(t.net_cash || 0)} icon={Banknote} testId="cash-report-net-cash" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Vendas por método</p>
            <div className="space-y-1.5">
              {(report?.by_method || []).map((m) => (
                <div key={m.method}>
                  <div className="flex items-center gap-2 text-sm" data-testid={`cash-report-method-${m.method}`}>
                    <span className="w-28 text-muted-foreground">{m.label}</span>
                    <div className="flex-1 h-2 rounded bg-muted overflow-hidden"><div className="h-full bg-primary" style={{ width: `${t.sales ? (m.total / t.sales) * 100 : 0}%` }} /></div>
                    <span className="font-mono w-28 text-right">{formatCurrency(m.total)}</span>
                  </div>
                  {m.method === "card" && (report?.by_tpa || []).map((g) => (
                    <div key={g.tpa_id || "none"} className="flex justify-between pl-6 text-xs text-muted-foreground" data-testid={`cash-period-tpa-${g.tpa_id || "none"}`}>
                      <span>› {g.name} ({g.count} venda(s))</span><span className="font-mono">{formatCurrency(g.total)}</span>
                    </div>
                  ))}
                </div>
              ))}
              {!report?.by_method?.length && <p className="text-sm text-muted-foreground">Sem vendas no período</p>}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Por dia</p>
            <div className="max-h-52 overflow-y-auto rounded border">
              <table className="w-full text-xs">
                <thead className="bg-muted/60 sticky top-0"><tr className="text-left"><th className="px-2 py-1.5">Dia</th><th className="px-2 py-1.5 text-right">Vendas</th><th className="px-2 py-1.5 text-right">Entradas</th><th className="px-2 py-1.5 text-right">Saídas</th></tr></thead>
                <tbody>
                  {(report?.by_day || []).map((d) => (
                    <tr key={d.date} className="border-t" data-testid={`cash-report-day-${d.date}`}>
                      <td className="px-2 py-1.5">{fmtDate(d.date)} <span className="text-muted-foreground">({d.sales_count})</span></td>
                      <td className="px-2 py-1.5 text-right font-mono">{formatCurrency(d.sales)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-emerald-600">{formatCurrency(d.entries)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-red-600">{formatCurrency(d.exits)}</td>
                    </tr>
                  ))}
                  {!report?.by_day?.length && <tr><td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">Sem movimentos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
